import { WallDatumAbstractionProvider } from "building-generation/elements/datums/datumAbstractionProviders";
import {
  collectWallFinishFaces,
  collectWallFinishFaces2,
  addWallPlaneTrimGeometry,
} from "building-generation/elements/wallAssemblies/wallFinishFaces";
import { isWallFramingAStud } from "building-generation/elements/walls/wallFramingUtils";
import { materialTransformationWithMirrorFallback } from "building-generation/generationTools/generateOpeningsTools";
import { uvGenerationForWallBRep } from "building-generation/utils/uvGenerationForEntities";
import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import { generatedBuildingEntityHandle } from "building-types/entities/buildingEntityPickHandle";
import type { BuildingGeometryEmitter, BuildingGeometryOptions } from "building-types/geometry/buildingGeometryTypes";
import { BuildingGeometryMode } from "building-types/geometry/buildingGeometryTypes";
import type { FacesWithMaterial } from "building-types/geometry/facesWithMaterial";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { WallFinishFaces } from "building-types/walls/wallFinishFaces";
import { WallFramingType, type WallFraming } from "building-types/walls/wallFramingTypes";
import type { Table } from "common/data/table";
import { MS_Color__C } from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialSrcFromPalette } from "doc/materialLookup";
import { makeLumberProfile } from "doc/utils/lumberTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceGroupNew } from "geom/brep";
import type { ClipFaceContext } from "geom/bspClipping";
import { clippingNodeNew, clipFaces, clipFacesWithPlanes } from "geom/bspClipping";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { UVGeneration } from "geom/uv";
import { INTERIOR_VIEW_CROSS_SECTION_COLOR } from "../constants/crossSection";
import { windowInfillsOnLevel } from "../walls/windowInfillsOnLevel";
import {
  buildUILayers,
  buildExteriorLayers,
  buildInteriorLayers,
  getInteriorGroupsGeometries,
  getExteriorGroupsGeometries,
} from "./buildingGeometryTools";

export type BuildingForWallGeometries = Pick<
  Building,
  | "levels"
  | "levelsAttributes"
  | "datumAbstractions"
  | "walls"
  | "wallSweeps"
  | "wallPlaneTrims"
  | "rooms"
  | "exteriorPalette"
  | "interiorPalette"
  | "mirror"
  | "windowUnits"
  | "windowInfills"
  | "enabledFeatures"
  | "buildingGraph"
  | "wallPlanes"
> & { doc: Pick<Doc, "datums"> };

type HomeSettingsForWallGeometries = Pick<HomeSettings, "wall" | "wallCatalog">;

export function getWindowInfillGeometries(
  levelId: string,
  building: BuildingForWallGeometries,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { exteriorPalette, interiorPalette, windowUnits, windowInfills } = building;
  const levelWindowInfills = windowInfillsOnLevel(levelId, windowUnits, windowInfills);
  const doAssignToClippingLayer = true;

  for (const [unit, infill] of levelWindowInfills) {
    if (entityTypes.has(FaceCategory.ROOM_WALL_INTERIOR)) {
      getInteriorGroupsGeometries({
        levelId,
        groups: [infill.interiorWallFill],
        palette: interiorPalette,
        room: unit.interiorRoom,
        faceCategory: FaceCategory.ROOM_WALL_INTERIOR,
        clip: doAssignToClippingLayer,
        entityHandleForSelectingReportingUnit: unit.entityHandleForSelectingReportingUnit,
        emitter,
      });
    }

    if (entityTypes.has(FaceCategory.ROOM_WALL_EXTERIOR)) {
      getExteriorGroupsGeometries({
        levelId,
        groups: [infill.exteriorWallFill],
        palette: exteriorPalette,
        faceCategory: FaceCategory.ROOM_WALL_EXTERIOR,
        setToExteriorLayer: doAssignToClippingLayer,
        pickHandle: unit.pickHandle,
        entityHandleForSelectingReportingUnit: unit.entityHandleForSelectingReportingUnit,
        emitter,
      });
    }
  }
}

export function getWallSweepGeometries(
  levelId: string,
  wallFinishFaces: WallFinishFaces,
  building: BuildingForWallGeometries,
  entityTypes: Set<FaceCategory>,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined, // Can be used to override the entityId
  emitter: BuildingGeometryEmitter,
): void {
  const { exteriorWallTrimFaces, exteriorWallExtrusions, exteriorWallBreps } = wallFinishFaces;

  const { exteriorPalette, mirror } = building;

  /* material trim faces*/
  if (entityTypes.has(FaceCategory.ROOM_WALL_BREP)) {
    for (const blockId in exteriorWallTrimFaces) {
      const faceArrayViewEntities = exteriorWallTrimFaces[blockId];
      for (const {
        faces,
        entityId,
        pickHandle,
        material,
        materialTransformation,
        descriptor,
        entityHandleForSelectingReportingUnit: entityHandleForSelectingReportingUnitFromTrimFaces,
      } of faceArrayViewEntities) {
        const resolvedEntityHandle =
          entityHandleForSelectingReportingUnit ?? entityHandleForSelectingReportingUnitFromTrimFaces;
        emitter.emit({
          geom: faceGroupNew(faces),
          faceCategory: FaceCategory.ROOM_WALL_BREP,
          threeLayers: buildExteriorLayers(),
          levelId,
          entityId,
          entityHandleForSelectingReportingUnit: resolvedEntityHandle,
          pickHandle: pickHandle ?? resolvedEntityHandle,
          materialSlot: material,
          materialSrc: materialSrcFromPalette(exteriorPalette, material),
          uvGen: uvGenerationForWallBRep(resolvedEntityHandle, materialTransformation),
          materialTransformation: materialTransformationWithMirrorFallback(materialTransformation, mirror),
          descriptor,
        });
      }
    }
  }

  /* extrusions */
  if (entityTypes.has(FaceCategory.ROOM_WALL_EXTRUSION)) {
    for (const blockId in exteriorWallExtrusions) {
      const extrusionViewEntities = exteriorWallExtrusions[blockId];
      for (const {
        extrusion,
        material,
        entityId,
        entityHandleForSelectingReportingUnit: entityHandleForSelectingReportingUnitFromExtrusions,
        pickHandle,
        materialTransformation,
        descriptor,
      } of extrusionViewEntities) {
        const resolvedEntityHandle =
          entityHandleForSelectingReportingUnit ?? entityHandleForSelectingReportingUnitFromExtrusions;
        emitter.emit({
          geom: extrusion,
          faceCategory: FaceCategory.ROOM_WALL_EXTRUSION,
          threeLayers: buildExteriorLayers(),
          levelId,
          entityId,
          entityHandleForSelectingReportingUnit: resolvedEntityHandle,
          pickHandle: pickHandle ?? resolvedEntityHandle,
          materialSlot: material,
          materialSrc: materialSrcFromPalette(exteriorPalette, material),
          uvGen: uvGenerationForWallBRep(resolvedEntityHandle, materialTransformation),
          materialTransformation: materialTransformationWithMirrorFallback(materialTransformation, mirror),
          descriptor,
        });
      }
    }
  }

  /* breps */
  if (entityTypes.has(FaceCategory.ROOM_WALL_BREP)) {
    for (const blockId in exteriorWallBreps) {
      const brepViewEntity = exteriorWallBreps[blockId];
      for (const {
        brep,
        material,
        entityId,
        entityHandleForSelectingReportingUnit: entityHandleForSelectingReportingUnitFromBreps,
        pickHandle,
        materialTransformation,
        descriptor,
      } of brepViewEntity) {
        const resolvedEntityHandle =
          entityHandleForSelectingReportingUnit ?? entityHandleForSelectingReportingUnitFromBreps;
        emitter.emit({
          geom: brep,
          faceCategory: FaceCategory.ROOM_WALL_BREP,
          threeLayers: buildExteriorLayers(),
          levelId,
          entityId,
          entityHandleForSelectingReportingUnit: resolvedEntityHandle,
          pickHandle: pickHandle ?? resolvedEntityHandle,
          materialSlot: material,
          materialSrc: materialSrcFromPalette(exteriorPalette, material),
          uvGen: uvGenerationForWallBRep(resolvedEntityHandle, materialTransformation),
          materialTransformation: materialTransformationWithMirrorFallback(materialTransformation, mirror),
          descriptor,
        });
      }
    }
  }
}

/**
 * Options for getWallFinishGeometries to customize face categories
 */
export interface WallFinishGeometriesOptions {
  /** Face category for exterior wall faces. Defaults to ROOM_WALL_EXTERIOR */
  exteriorFaceCategory?: FaceCategory;
  /** Face category for interior wall faces. Defaults to ROOM_WALL_INTERIOR */
  interiorFaceCategory?: FaceCategory;
}

export function getWallFinishGeometries(
  levelId: string,
  wallFinishFaces: WallFinishFaces,
  building: BuildingForWallGeometries,
  settings: HomeSettingsForWallGeometries,
  options: BuildingGeometryOptions,
  entityTypes: Set<FaceCategory>,
  wallFinishOptions: WallFinishGeometriesOptions,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  emitter: BuildingGeometryEmitter,
): void {
  const { exteriorWallFaces, exteriorWallCapFaces, interiorWallFaces, interiorWallCapFaces, coreFinishFaces } =
    wallFinishFaces;

  const {
    exteriorFaceCategory = FaceCategory.ROOM_WALL_EXTERIOR,
    interiorFaceCategory = FaceCategory.ROOM_WALL_INTERIOR,
  } = wallFinishOptions;

  let orphanedRoomFaceRoomType: RoomType | undefined = undefined;
  let useWallFaceIntegerIdForInteriorFaces = false;
  if (building.enabledFeatures.wallPlanes) {
    /**
     * @NOTE - WALLS V2 creates many orphaned room faces.
     * These faces must be rendered using their palette material slot,
     * unlike orphaned room faces which come from roof walls.
     *
     * We need to pass a room type into the material src function
     * even though it is not used, since all WALLS V2 faces have a finish material
     * but we need to pass a room type to the material src function to ensure that
     * the correct material is used.
     *
     * @TODO - we need to remove this after walls v2 is in production, and set all orphaned room faces to this type.
     * this will break some baselines, but those baselines are using the wrong material slot.
     */
    orphanedRoomFaceRoomType = RoomType.GENERAL;
    useWallFaceIntegerIdForInteriorFaces = true;
  }

  const { rooms, exteriorPalette, interiorPalette, mirror } = building;
  const { exteriorWallMaterial } = settings.wall;
  const topBottomMatSlot: ExteriorMaterial = "trimExterior0";
  const topBottomMatColor = INTERIOR_VIEW_CROSS_SECTION_COLOR;

  const getMaterialForExternalWalls = (material?: ExteriorMaterial): ExteriorMaterial => {
    return material ?? exteriorWallMaterial;
  };

  const addTopBottomFaces = (faces: Face[]) => {
    emitter.emit({
      geom: faceGroupNew(faces),
      faceCategory: exteriorFaceCategory,
      threeLayers: buildUILayers(),
      levelId,
      materialSlot: topBottomMatSlot,
      materialSrc: materialSrcFromPalette(exteriorPalette, topBottomMatSlot),
      overrideMaterialWithColor: topBottomMatColor,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      dontClipWhenRenderedWithOverrideMaterial: true,
      entityHandleForSelectingReportingUnit,
    });
  };

  const addExteriorFaces = (exteriorFaces: { [blockId: string]: FacesWithMaterial[] }) => {
    for (const blockId in exteriorFaces) {
      const facesWithMaterials = exteriorFaces[blockId];
      for (const {
        faces,
        entityId,
        descriptor,
        material,
        pickHandle,
        entityHandleForSelectingReportingUnit: entityHandleForSelectingReportingUnitFromFaces,
        materialTransformation,
      } of facesWithMaterials) {
        const finalMaterial = getMaterialForExternalWalls(material as ExteriorMaterial);

        emitter.emit({
          geom: faceGroupNew(faces),
          faceCategory: exteriorFaceCategory,
          threeLayers: buildExteriorLayers(),
          levelId,
          entityId,
          descriptor,
          pickHandle,
          entityHandleForSelectingReportingUnit:
            entityHandleForSelectingReportingUnit ?? entityHandleForSelectingReportingUnitFromFaces,
          materialSlot: finalMaterial,
          materialSrc: materialSrcFromPalette(exteriorPalette, finalMaterial),
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          materialTransformation: materialTransformationWithMirrorFallback(materialTransformation, mirror),
        });
      }
    }
  };

  const addInteriorFaces = (interiorFaces: { [roomId: string]: FacesWithMaterial[] }) => {
    for (const roomId in interiorFaces) {
      const room = rooms[roomId];

      /**
       * Some walls are produced by rooms that don't exist in building.rooms. These
       * are often synthetic rooms like attic room, rooms from roof walls or void rooms.
       * We need to pass a room type into the material src function to ensure that
       * the correct material is used, even though the code path will ignore it if the finish material is provided.
       */
      const wallRoomType = room?.type ?? orphanedRoomFaceRoomType;
      const wallMaterial = room?.wallMaterial;

      let threeLayers: number[];
      if (room) {
        threeLayers = buildInteriorLayers();
      } else {
        // Orphaned room faces (room doesn't exist in building.rooms)
        // Use exterior layers as fallback since we don't have a room index
        threeLayers = buildExteriorLayers();
      }

      const facesWithMaterials = interiorFaces[roomId];

      // Compute handle for this room if not provided
      const handleForRoom = entityHandleForSelectingReportingUnit ?? roomEntityHandleForSelectingReportingUnit(room);

      for (const { faces, pickHandle, material, materialTransformation } of facesWithMaterials) {
        const finalMaterial = material ?? wallMaterial;

        emitter.emit({
          geom: faceGroupNew(faces),
          faceCategory: interiorFaceCategory,
          threeLayers,
          levelId,
          entityId: roomId,
          pickHandle: useWallFaceIntegerIdForInteriorFaces ? pickHandle : room?.pickHandle,
          entityHandleForSelectingReportingUnit: handleForRoom,
          materialSlot: finalMaterial,
          materialSrc: materialSrcFromPalette(interiorPalette, finalMaterial, wallRoomType),
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          materialTransformation: materialTransformationWithMirrorFallback(
            materialTransformation,
            mirror,
            room?.componentTransform,
          ),
        });
      }
    }
  };

  if (options.mode === BuildingGeometryMode.BUILDING_VIEWER && entityTypes.has(exteriorFaceCategory)) {
    addTopBottomFaces(coreFinishFaces);
  }

  if (entityTypes.has(exteriorFaceCategory)) {
    addExteriorFaces(exteriorWallFaces);
    addExteriorFaces(exteriorWallCapFaces);
  }

  if (entityTypes.has(interiorFaceCategory)) {
    addInteriorFaces(interiorWallFaces);
    addInteriorFaces(interiorWallCapFaces);
  }
}

export enum WallGeometryFilter {
  /** Generate wall finish faces only (ROOM_WALL_EXTERIOR, ROOM_WALL_INTERIOR) */
  WALL_FINISH_ONLY = "WALL_FINISH_ONLY",
  /** Generate wall sweep/trim faces only (ROOM_WALL_BREP, ROOM_WALL_EXTRUSION) */
  WALL_SWEEP_ONLY = "WALL_SWEEP_ONLY",
  /** Generate all wall geometry types (default) */
  ALL = "ALL",
}

export function getWallGeometries(
  building: BuildingForWallGeometries,
  settings: HomeSettingsForWallGeometries,
  options: BuildingGeometryOptions,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
  filter: WallGeometryFilter = WallGeometryFilter.ALL,
): void {
  const {
    levels,
    levelsAttributes,
    walls,
    wallSweeps,
    wallPlaneTrims,
    mirror,
    interiorPalette,
    exteriorPalette,
    datumAbstractions,
    enabledFeatures,
  } = building;
  const { wall: wallSettings, wallCatalog: wallCatalogSettings } = settings;

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);
  let wallPlaneTrimGeometryAdded = false;
  for (const levelId in levels) {
    const wallFinishFaces =
      enabledFeatures.wallPlanes || enabledFeatures.collectWallFinishFaces2
        ? collectWallFinishFaces2({
            wallDatumAbstractionProvider,
            wallSystem: walls[levelId],
            wallSweeps: wallSweeps[levelId],
            wallCatalogSettings,
            mirror,
          })
        : collectWallFinishFaces({
            wallDatumAbstractionProvider,
            wallSystem: walls[levelId],
            wallSweeps: wallSweeps[levelId],
            interiorPalette,
            exteriorPalette,
            wallSettings,
            wallCatalogSettings,
            mirror,
            enableLegacyDepthOffsets: options.mode !== BuildingGeometryMode.EXPORT,
          });

    if (!wallPlaneTrimGeometryAdded && enabledFeatures.wallPlanes) {
      addWallPlaneTrimGeometry(wallFinishFaces, wallPlaneTrims);
      wallPlaneTrimGeometryAdded = true;
    }

    if (filter !== WallGeometryFilter.WALL_SWEEP_ONLY) {
      getWallFinishGeometries(
        levelId,
        wallFinishFaces,
        building,
        settings,
        options,
        entityTypes,
        {},
        undefined,
        emitter,
      );
      getWindowInfillGeometries(levelId, building, entityTypes, emitter);
    }

    if (
      filter !== WallGeometryFilter.WALL_FINISH_ONLY &&
      (entityTypes.has(FaceCategory.ROOM_WALL_BREP) || entityTypes.has(FaceCategory.ROOM_WALL_EXTRUSION))
    ) {
      getWallSweepGeometries(levelId, wallFinishFaces, building, entityTypes, undefined, emitter);
    }
  }
}

const wallFramingMaterialSrcs: Record<WallFramingType, MaterialSrc> = {
  [WallFramingType.BOTTOM_PLATE]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Brown },
  [WallFramingType.TOP_PLATE]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Brown },
  [WallFramingType.STANDARD_STUD]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  [WallFramingType.KING_STUD]: { ...MS_Color__C, colorId: DMC.SystemColors.Orange },
  [WallFramingType.JACK_STUD]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Yellow },
  [WallFramingType.BOTTOM_CRIPPLE_STUD]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  [WallFramingType.TOP_CRIPPLE_STUD]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  [WallFramingType.BOTTOM_SILL_PLATE]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  [WallFramingType.TOP_SILL_PLATE]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  [WallFramingType.HEADER]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Brown },
  [WallFramingType.BLOCKING]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
};

interface GetWallFramingGeometriesOptions {
  wallFramingSystems: Table<WallFraming[]>;
  faceCategory: FaceCategory;
  debugColors: boolean;
  emitter: BuildingGeometryEmitter;
  /** If true, this geometry should be hidden in interior/dollhouse views (e.g. gable end wall framing) */
  aboveLevel?: boolean;
}

export function getWallFramingGeometries({
  wallFramingSystems,
  faceCategory,
  debugColors,
  emitter,
  aboveLevel,
}: GetWallFramingGeometriesOptions): void {
  for (const levelId in wallFramingSystems) {
    for (const { framings, studClippingBSP } of wallFramingSystems[levelId]) {
      for (const framing of framings) {
        const clippingBSP = isWallFramingAStud(framing) ? studClippingBSP : null;

        const { profileDescriptor, rails, type } = framing;

        const materialSrc = debugColors
          ? wallFramingMaterialSrcs[type]
          : { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange };

        const profile = [makeLumberProfile(profileDescriptor)];

        if (!rails) {
          continue;
        }

        const studClippingOptions: ClipFaceContext | null = clippingBSP
          ? {
              clippingNodes: [clippingNodeNew(clippingBSP, true)],
              coplanarsToFront: false,
              epsilon: MULTIGABLE_BSP_EPSILON,
            }
          : null;

        for (const { rail, nX, nY, nZ, clipPlanes, id } of rails) {
          let faces = extrudeFaceAlongPolyLine3(profile, rail, nX, nY, nZ).faces;

          if (studClippingOptions) {
            faces = clipFaces({
              faces,
              options: studClippingOptions,
            });
          }

          if (clipPlanes && clipPlanes.length > 0) {
            faces = clipFacesWithPlanes({
              faces,
              planes: clipPlanes,
              addCapFaces: true,
            });
          }

          emitter.emit({
            geom: faceGroupNew(faces),
            faceCategory,
            threeLayers: buildExteriorLayers(),
            levelId,
            pickHandle: generatedBuildingEntityHandle("LUMBER", id),
            materialSlot: "framing",
            materialSrc,
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            aboveLevel,
          });
        }
      }
    }
  }
}
